import { startTransition, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type {
  SetQueryParamValue,
  UseQueryParamValueOptions,
} from './useQueryParamValue.types';

export const useQueryParamValue = ({
  defaultValue = null,
  paramName,
  shouldDeleteDefaultValue = true,
}: UseQueryParamValueOptions) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const value = useMemo(
    () => searchParams.get(paramName) ?? defaultValue,
    [defaultValue, paramName, searchParams]
  );

  const setValue = useCallback<SetQueryParamValue>(
    (nextValue, { replace = false } = {}) => {
      startTransition(() => {
        setSearchParams(prevSearchParams => {
          const nextSearchParams = new URLSearchParams(prevSearchParams);
          const shouldDeleteValue = !nextValue
            || (shouldDeleteDefaultValue && nextValue === defaultValue);

          if (shouldDeleteValue) {
            nextSearchParams.delete(paramName);
          } else {
            nextSearchParams.set(paramName, nextValue);
          }

          if (nextSearchParams.toString() === prevSearchParams.toString()) {
            return prevSearchParams;
          }

          return nextSearchParams;
        }, { replace });
      });
    },
    [defaultValue, paramName, setSearchParams, shouldDeleteDefaultValue]
  );

  return {
    setValue,
    value,
  };
};
